import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Shield, Star, MapPin, Calendar, MessageCircle, CreditCard,
  QrCode, Home, Award, Users, Zap, Lock, DollarSign, Briefcase, ArrowRight,
  Wand2, Heart,
} from "lucide-react";

/**
 * HowItWorks — Phase 44 simplification.
 *
 * Single-promise customer 3-step journey leads. Audience-specific (Hosts /
 * Concierges / Badges) sections live below in concise blocks so the page
 * doesn't read like a walls-of-text spec sheet anymore.
 *
 * Design system: White primary, soft champagne borders, deep navy text,
 * coral CTAs, ocean blue AI accent. Less beige. Mobile-first responsive.
 */

const NAVY = "#071A2D";
const EMERALD = "#064E3B";
const GOLD = "#C9A45C";
const CORAL = "#FF7A45";
const OCEAN = "#00AEEF";

// Premium section selector pills — kept light & secondary so the eye lands
// on the 3-step customer journey above first.
const NAV_PILLS = [
  { hash: "customers", label: "Customers",  icon: Users,      accent: OCEAN },
  { hash: "concierges", label: "Concierges", icon: Briefcase,  accent: GOLD },
  { hash: "hosts",     label: "Hosts",      icon: Home,       accent: EMERALD },
  { hash: "badges",    label: "Trust Badges", icon: Shield,   accent: OCEAN },
];

function SectionNav() {
  const [active, setActive] = useState(() =>
    (typeof window !== "undefined" && window.location.hash.replace("#", "")) || ""
  );
  useEffect(() => {
    const onHash = () => setActive(window.location.hash.replace("#", ""));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return (
    <div className="flex flex-wrap gap-2" data-testid="section-nav">
      {NAV_PILLS.map(({ hash, label, icon: Icon, accent }) => {
        const isActive = active === hash;
        return (
          <a
            key={hash}
            href={`#${hash}`}
            data-testid={`section-nav-${hash}`}
            onClick={() => setActive(hash)}
            className="group inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-medium transition-all duration-200 border focus:outline-none"
            style={{
              background: isActive ? accent : "#FFFFFF",
              color: isActive ? "#FFFFFF" : NAVY,
              borderColor: isActive ? accent : "rgba(7,26,45,0.10)",
            }}
          >
            <Icon size={13} />
            {label}
          </a>
        );
      })}
    </div>
  );
}

// === 3-step journey ===========================================================

const JOURNEY = [
  {
    n: "01",
    icon: Wand2,
    accent: OCEAN,
    title: "Tell TREL your vision",
    desc: "Describe what you want — a trip, a celebration, a special moment, a reservation, or a lifestyle request. Words. Not forms.",
    snippets: [
      "Plan my anniversary weekend in Miami.",
      "Coordinate my family vacation in Italy.",
      "Find my perfect VIP night out for 8.",
    ],
  },
  {
    n: "02",
    icon: Sparkles,
    accent: GOLD,
    title: "AI refines your request",
    desc: "TREL AI organizes your idea into a clear concierge brief — duration, guests, budget, vibe — so the trusted pros understand exactly what you need.",
    snippets: [
      "Structured plan in 30s.",
      "AI asks only the questions that matter.",
      "You stay in control — edit anything.",
    ],
  },
  {
    n: "03",
    icon: Shield,
    accent: EMERALD,
    title: "Your Concierge handles everything",
    desc: "Verified TREL Concierges manage planning, reservations, vendors, transportation, surprises, logistics, and details. One point of contact, end-to-end.",
    snippets: [
      "Verified · TREL Certified · Identity confirmed.",
      "One contact for the whole experience.",
      "You show up. They've handled the rest.",
    ],
  },
];

function JourneyCard({ step, idx }) {
  const Icon = step.icon;
  return (
    <article
      data-testid={`how-step-${step.n}`}
      className="relative rounded-3xl bg-white p-7 sm:p-8 border border-stone-200 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_28px_60px_-32px_rgba(28,25,23,0.18)] hover:shadow-[0_2px_4px_rgba(28,25,23,0.06),0_36px_72px_-32px_rgba(28,25,23,0.24)] transition-all duration-300"
    >
      {/* Floating step glyph */}
      <div className="flex items-start justify-between">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: `${step.accent}14`, color: step.accent }}
        >
          <Icon size={26} />
        </div>
        <span
          className="font-serif text-5xl leading-none"
          style={{ color: `${step.accent}55` }}
        >
          {step.n}
        </span>
      </div>

      <h3 className="font-serif text-2xl mt-6 text-[#071A2D]">{step.title}</h3>
      <p className="mt-3 text-[#071A2D]/70 leading-relaxed">{step.desc}</p>

      <ul className="mt-5 space-y-1.5 text-sm text-[#071A2D]/75">
        {step.snippets.map((s) => (
          <li key={s} className="flex items-start gap-2">
            <span
              className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: step.accent }}
            />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

// === Secondary blocks =========================================================

const Bullet = ({ children, accent = OCEAN }) => (
  <li className="flex items-start gap-2 py-0.5 text-[14px] text-[#071A2D]/80">
    <span
      className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
      style={{ background: accent }}
    />
    <span>{children}</span>
  </li>
);

function SecondarySection({ id, eyebrow, title, lede, children, cta, ctaTo, testid }) {
  return (
    <section
      id={id}
      data-testid={testid}
      className="max-w-6xl mx-auto px-6 py-20 scroll-mt-24"
    >
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#064E3B]">
        {eyebrow}
      </div>
      <h2 className="font-serif text-3xl md:text-4xl mt-3 text-[#071A2D] leading-tight">
        {title}
      </h2>
      {lede && (
        <p className="mt-4 text-[#071A2D]/70 max-w-2xl leading-relaxed">{lede}</p>
      )}
      <div className="mt-8">{children}</div>
      {cta && ctaTo && (
        <div className="mt-10">
          <Link to={ctaTo} className="btn-coral inline-flex items-center gap-2">
            {cta} <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </section>
  );
}

const BadgeCard = ({ color, name, sub, cost, desc }) => (
  <div className="rounded-3xl p-7 bg-white border border-stone-200 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_24px_56px_-32px_rgba(28,25,23,0.18)]">
    <div className="flex items-center justify-between">
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold"
        style={{ background: color, color: "#FFFFFF" }}
      >
        {name}
      </span>
      <span className="text-[12px] text-[#071A2D]/55">{cost}</span>
    </div>
    <p className="mt-4 text-[12px] uppercase tracking-[0.18em] text-[#071A2D]/55">{sub}</p>
    <p className="mt-3 text-[14px] text-[#071A2D]/80 leading-relaxed">{desc}</p>
  </div>
);

// === Page ====================================================================

export default function HowItWorks() {
  return (
    <div className="bg-white" data-testid="how-it-works-page">
      {/* HERO — single-sentence promise, 3-step journey teaser */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 sm:pt-20">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#064E3B]">
          How TREL works
        </div>
        <h1 className="font-serif text-5xl md:text-7xl mt-3 text-[#071A2D] leading-[1.02]">
          A simple journey.{" "}
          <span className="italic text-[#064E3B]">An unforgettable result.</span>
        </h1>
        <p className="mt-6 text-lg text-[#071A2D]/70 max-w-2xl leading-relaxed">
          You describe what you want. Our AI organizes the brief. A trusted
          TREL Concierge handles every detail — start to finish.
        </p>
        <div className="mt-8">
          <SectionNav />
        </div>
      </section>

      {/* CUSTOMER 3-STEP JOURNEY — the hero of this page */}
      <section
        id="customers"
        className="max-w-6xl mx-auto px-6 py-12 scroll-mt-24"
        data-testid="how-customer-journey"
      >
        <div className="grid lg:grid-cols-3 gap-6">
          {JOURNEY.map((s, i) => (
            <JourneyCard key={s.n} step={s} idx={i} />
          ))}
        </div>

        {/* End message — the brand promise */}
        <div
          className="mt-12 rounded-3xl bg-[#FFF8EE] border border-[#C9A45C]/30 px-8 py-10 text-center"
          data-testid="how-end-message"
        >
          <Heart size={22} className="mx-auto text-[#FF7A45]" />
          <p className="mt-3 font-serif text-2xl md:text-3xl text-[#071A2D] leading-tight">
            One request. One Concierge.{" "}
            <span className="italic text-[#064E3B]">One unforgettable moment.</span>
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai"
              data-testid="how-cta-primary"
              className="btn-coral inline-flex items-center gap-2"
            >
              Start with AI Concierge <ArrowRight size={16} />
            </Link>
            <Link
              to="/explore"
              data-testid="how-cta-secondary"
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 rounded-full border border-[#071A2D]/15 hover:border-[#071A2D]/35 hover:bg-stone-50 transition text-[#071A2D]"
            >
              Or meet our Concierges
            </Link>
          </div>
        </div>
      </section>

      {/* CONCIERGES — concise (was a sprawling section before) */}
      <div className="bg-[#FFF8EE] border-y border-[#C9A45C]/15">
        <SecondarySection
          id="concierges"
          eyebrow="For Concierges"
          title="Earn by delivering unforgettable experiences."
          lede="Verified TREL Concierges power the marketplace — private chefs, planners, drivers, hosts, photographers, lifestyle experts. You set your prices. We bring the customers."
          cta="Become a Concierge"
          ctaTo="/become-provider"
          testid="how-concierges"
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Users, title: "Create your profile", body: "Services, photos, pricing, service area." },
              { icon: Shield, title: "Get TREL Certified (free)", body: "Identity + training + standards, ~10 min." },
              { icon: Calendar, title: "Receive bookings", body: "Customers + host guests find you on TREL." },
              { icon: MessageCircle, title: "Deliver the experience", body: "Manage everything from one dashboard." },
              { icon: DollarSign, title: "Earn 80% + 100% of tips", body: "Tips never share commission. Direct or host." },
              { icon: Award, title: "Grow with TREL+ ($29/mo)", body: "AI tools · analytics · marketing · network." },
            ].map((row) => {
              const Icon = row.icon;
              return (
                <div
                  key={row.title}
                  className="rounded-2xl bg-white border border-stone-200 p-5 shadow-[0_1px_2px_rgba(28,25,23,0.04)]"
                >
                  <Icon size={18} className="text-[#C9A45C]" />
                  <div className="font-medium mt-3 text-[#071A2D]">{row.title}</div>
                  <p className="mt-1 text-[13px] text-[#071A2D]/70 leading-snug">{row.body}</p>
                </div>
              );
            })}
          </div>
        </SecondarySection>
      </div>

      {/* HOSTS — concise */}
      <SecondarySection
        id="hosts"
        eyebrow="For Hosts"
        title="Turn every stay into a premium concierge moment."
        lede="Airbnb hosts and property owners earn referral income when their guests book experiences through a custom TREL concierge page on your property."
        cta="Become a Host"
        ctaTo="/become-host"
        testid="how-hosts"
      >
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <ol className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Home, title: "List your property", body: "Any US stay — Airbnbs, villas, cabins." },
              { icon: QrCode, title: "Generate your concierge page", body: "Unique link + QR for guest sharing." },
              { icon: MessageCircle, title: "Share with guests", body: "Welcome packets, Airbnb messages, in-room displays." },
              { icon: DollarSign, title: "Earn referral income", body: "Concierge 80% · Host 5% · TREL 15%. Tips 100% to Concierge." },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.title}
                  className="rounded-2xl bg-white border border-stone-200 p-5 shadow-[0_1px_2px_rgba(28,25,23,0.04)] flex items-start gap-3"
                >
                  <span className="w-10 h-10 rounded-xl bg-[#064E3B]/8 flex items-center justify-center text-[#064E3B] shrink-0">
                    <Icon size={16} />
                  </span>
                  <div>
                    <div className="font-medium text-[#071A2D]">{s.title}</div>
                    <p className="mt-1 text-[13px] text-[#071A2D]/70 leading-snug">{s.body}</p>
                  </div>
                </li>
              );
            })}
          </ol>
          <aside className="lg:col-span-5 rounded-3xl bg-white border border-[#C9A45C]/35 p-6 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-22px_rgba(28,25,23,0.15)]">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
              Host upside
            </div>
            <ul className="mt-3 space-y-1.5">
              <Bullet accent={EMERALD}>Passive income on every guest stay</Bullet>
              <Bullet accent={EMERALD}>Better guest reviews and stays</Bullet>
              <Bullet accent={EMERALD}>Premium hospitality with zero staff</Bullet>
              <Bullet accent={EMERALD}>Stand out from generic rentals</Bullet>
            </ul>
            <p className="mt-5 text-[12px] text-[#071A2D]/55">
              Hosts do <strong>not</strong> list overnight stays on TREL — TREL adds the
              concierge experience on top of your existing platform.
            </p>
          </aside>
        </div>
      </SecondarySection>

      {/* BADGES — light, scannable */}
      <div className="bg-[#FFF8EE] border-y border-[#C9A45C]/15">
        <SecondarySection
          id="badges"
          eyebrow="The TREL trust ladder"
          title="Trust earned. Never bought."
          lede="Every TREL Concierge progresses through visible trust levels. No paid badges, no &lsquo;Top Pro&rsquo; banners you can purchase."
          testid="how-badges"
        >
          <div className="grid md:grid-cols-3 gap-5">
            <BadgeCard
              color={OCEAN}
              name="✓ Identity Verified"
              sub="Trust · Level 1"
              cost="Free"
              desc="Secure third-party identity check. We never store ID photos."
            />
            <BadgeCard
              color={EMERALD}
              name="⭐ TREL Certified"
              sub="Trust · Level 2"
              cost="Free · ~10 min"
              desc="Training + service-standards agreement. Required to accept bookings."
            />
            <BadgeCard
              color={GOLD}
              name="💎 TREL Elite"
              sub="Trust · Level 3 · Optional"
              cost="$60 one-time"
              desc="Enhanced background screening via Checkr. Recommended for corporate & luxury experiences."
            />
          </div>
          <div className="mt-8 rounded-3xl bg-[#071A2D] text-white p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#C9A45C] flex items-center gap-2">
              <Lock size={12} /> Trust and growth are separate by design.
            </div>
            <p className="mt-3 text-white/75 leading-relaxed">
              Trust badges represent customer confidence — earned through identity,
              training, and screening. TREL+ ($29/mo) is a paid growth subscription
              that adds tools, never credibility. Concierges compete on quality.
            </p>
          </div>
        </SecondarySection>
      </div>

      {/* CLOSING CTA */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-[#071A2D]">
          Ready to begin?
        </h2>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/ai" className="btn-coral">Plan a moment</Link>
          <Link to="/become-host" className="btn-outline-emerald">Host my property</Link>
          <Link to="/become-provider" className="btn-outline-emerald">Become a Concierge</Link>
        </div>
      </section>
    </div>
  );
}
